import React, { useState } from 'react';
import dayjs from 'dayjs';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { cn } from '../lib/utils';

type DatePickerFieldProps = {
  label: string;
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  className?: string;
};

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const selectedDate = value ? dayjs(value).toDate() : undefined;

  const isDisabled = (date: Date) => {
    const day = dayjs(date).startOf('day');
    if (min && day.isBefore(dayjs(min).startOf('day'), 'day')) return true;
    if (max && day.isAfter(dayjs(max).startOf('day'), 'day')) return true;
    return false;
  };

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-9 min-w-[160px] px-3 text-sm rounded-lg border border-slate-300 bg-white text-left flex items-center justify-between gap-2 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <span className={value ? 'text-slate-800' : 'text-slate-400'}>
              {value ? dayjs(value).format('DD MMM YYYY') : 'Select date'}
            </span>
            <CalendarMonthIcon className="!text-base text-slate-500" fontSize="small" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="z-[80] w-auto p-0 border border-slate-200 bg-white text-slate-900 shadow-xl rounded-xl overflow-hidden"
        >
          <div className="bg-white">
            <Calendar
              mode="single"
              selected={selectedDate}
              defaultMonth={selectedDate}
              onSelect={(date) => {
                if (!date) return;
                onChange(dayjs(date).format('YYYY-MM-DD'));
                setOpen(false);
              }}
              disabled={isDisabled}
              showOutsideDays={false}
              className="p-3 bg-white text-slate-900"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DatePickerField;
